// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useState, useMemo } from 'react';
import { Button } from '../Button';
import { GamePreview } from './GamePreview';
import type { GameSummary } from '../../types/game';

interface FeaturedGamesProps {
  games: GameSummary[];
  onSelectGame: (gameId: string) => void;
  onBrowseAll: () => void;
}

// Map game IDs to emoji icons
const GAME_ICONS: Record<string, string> = {
  reaction: '🃏',
  terrible_psychiatrist: '🧠',
  terrible_psychologist: '🧠',
  debate_club: '⚔️',
  confession_booth: '💬',
  speed_diagnosis: '🎯',
  empathy_engine: '❤️',
  prompt_builder: '✏️',
  ink_blot_test: '🖌️',
  language_pack: '🌍',
  motivational: '🌟',
  reverse_therapy_diagnosis: '🔄',
  group_therapy: '👥',
  daily_checkin: '📅',
  therapy_bingo: '🎲',
  icebreaker: '❄️',
  social_proof_marketplace: '📊',
  peer_review: '⭐',
  mindfulness_meditation: '🧘',
  storytelling_circle: '📖',
};

export function FeaturedGames({ games, onSelectGame, onBrowseAll }: FeaturedGamesProps) {
  const [selectedGameForPreview, setSelectedGameForPreview] = useState<GameSummary | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Intelligently select featured games to showcase variety
  // Rule: Default first, then diverse categories
  const getGameCategory = (gameId: string): string => {
    const categories: Record<string, string> = {
      // Party games
      reaction: 'party',
      terrible_psychiatrist: 'party',
      terrible_psychologist: 'party',
      confession_booth: 'party',
      therapy_bingo: 'party',

      // Strategy games
      debate_club: 'strategy',
      social_proof_marketplace: 'strategy',
      prompt_builder: 'strategy',

      // Creative games
      ink_blot_test: 'creative',
      storytelling_circle: 'creative',
      language_pack: 'creative',

      // Social/wellness games
      empathy_engine: 'social',
      group_therapy: 'social',
      daily_checkin: 'social',
      icebreaker: 'social',
      mindfulness_meditation: 'social',
      speed_diagnosis: 'social',
      reverse_therapy_diagnosis: 'social',
      motivational: 'social',
      peer_review: 'social',
    };
    return categories[gameId] || 'party';
  };

  // Get unique categories for filter
  const allCategories = useMemo(() => {
    const cats = new Set(games.map(g => getGameCategory(g.game_id)));
    return ['all', ...Array.from(cats).sort()];
  }, [games]);

  // Filter games by search and category
  const featured = useMemo(() => {
    let filtered = [...games];
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(g => getGameCategory(g.game_id) === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        g =>
          g.display_name.toLowerCase().includes(q) ||
          (g.description || '').toLowerCase().includes(q) ||
          (g.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    return filtered;
  }, [games, searchQuery, selectedCategory]);

  // Helper to get category color for game
  const getCategoryColor = (gameId: string): { primary: string; accent: string } => {
    const category = getGameCategory(gameId);
    // Use theme CSS variables that adapt to selected color scheme
    const colors: Record<string, { primary: string; accent: string }> = {
      party: {
        primary: 'var(--color-primary)',
        accent: 'var(--color-secondary)',
      },
      strategy: {
        primary: 'var(--color-secondary)',
        accent: 'var(--color-accent)',
      },
      creative: {
        primary: 'var(--color-accent)',
        accent: 'var(--color-primary)',
      },
      social: {
        primary: 'var(--color-success)',
        accent: 'var(--color-accent)',
      },
    };
    return colors[category] || colors.party;
  };

  return (
    <>
      <div className="glass rounded-lg p-8 mb-8" data-testid="featured-games">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2
            className="font-display text-3xl"
            style={{ color: 'var(--color-primary)' }}
            data-testid="featured-games-title"
          >
            Games
          </h2>
          <Button
            onClick={onBrowseAll}
            variant="primary"
            size="md"
            data-testid="featured-games-browse-all"
          >
            See All {games.length}
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            placeholder="Search games..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg text-sm"
            style={{
              backgroundColor: 'var(--color-surface-hover)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-accent)',
            }}
            data-testid="games-search-input"
          />
          <div className="flex gap-2 flex-wrap">
            {allCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-all"
                style={{
                  backgroundColor:
                    selectedCategory === cat
                      ? 'var(--color-primary)'
                      : 'var(--color-surface-hover)',
                  color:
                    selectedCategory === cat
                      ? 'var(--color-text-on-primary, #fff)'
                      : 'var(--color-text-secondary)',
                }}
                data-testid={`games-filter-${cat}`}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Game Grid - responsive: 1 col phone, 2 col sm, 3 col tablet, 4 col desktop */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          data-testid="featured-games-grid"
        >
          {featured.map(game => {
            const icon = GAME_ICONS[game.game_id] || '🎮';
            const tags = (game.tags || []).slice(0, 2);
            const { primary } = getCategoryColor(game.game_id);

            return (
              <div
                key={game.game_id}
                className="glass rounded-lg overflow-hidden border-2 hover:shadow-lg transition-all"
                style={{ borderColor: 'var(--color-accent)' }}
                data-testid={`featured-game-card-${game.game_id}`}
              >
                {/* Top accent bar with primary color */}
                <div
                  className="h-2"
                  style={{
                    backgroundColor: primary,
                  }}
                />

                <div className="p-4">
                  {/* Icon and Title */}
                  <div className="text-center mb-4">
                    <div className="text-5xl mb-2" data-testid={`game-card-icon-${game.game_id}`}>
                      {icon}
                    </div>
                    <h3
                      className="font-bold text-lg line-clamp-2"
                      style={{ color: 'var(--color-text-primary)' }}
                      data-testid={`game-card-title-${game.game_id}`}
                    >
                      {game.display_name}
                    </h3>
                  </div>

                  {/* Description */}
                  <p
                    className="text-sm mb-3 line-clamp-2 h-10"
                    style={{ color: 'var(--color-text-secondary)' }}
                    data-testid={`game-card-description-${game.game_id}`}
                  >
                    {game.description}
                  </p>

                  {/* Player Count and Tags on same row */}
                  <div className="flex items-center justify-between mb-4 gap-2">
                    {/* Player Count */}
                    <div
                      className="text-xs font-bold"
                      style={{ color: 'var(--color-text-primary)' }}
                      data-testid={`game-card-players-${game.game_id}`}
                    >
                      👥 {game.min_players}-{game.max_players}
                    </div>

                    {/* Tags - aligned right */}
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-end">
                        {tags.map(tag => (
                          <span
                            key={tag}
                            className="text-xs font-medium px-2 py-0.5 rounded"
                            style={{
                              backgroundColor: 'var(--color-surface-hover)',
                              color: 'var(--color-text-secondary)',
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setSelectedGameForPreview(game)}
                      variant="secondary"
                      size="sm"
                      className="flex-1 text-xs"
                      data-testid={`game-card-preview-button-${game.game_id}`}
                    >
                      👁️ Preview
                    </Button>
                    <Button
                      onClick={() => onSelectGame(game.game_id)}
                      variant="success"
                      size="sm"
                      className="flex-1 text-xs"
                      data-testid={`game-card-play-button-${game.game_id}`}
                    >
                      ▶️ Play
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Game Preview Modal */}
      {selectedGameForPreview && (
        <GamePreview
          game={selectedGameForPreview}
          isOpen={!!selectedGameForPreview}
          onClose={() => setSelectedGameForPreview(null)}
        />
      )}
    </>
  );
}
